import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { agentLink, copy } from "@/components/admin/AgentsPanel";
import { AgentApplicationDialog } from "@/components/agent/AgentApplicationDialog";
import { ChangePassword } from "@/components/agent/ChangePassword";

const TABS = [["F_170_185", "170–185 cm"], ["F_150_169", "150–169 cm"], ["OTHER", "Altre"]];
const CONTRACT = { sent: "Contratto inviato", signed: "Contratto firmato", cancelled: "Contratto annullato" };

export default function Agent() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("F_170_185");
  const [pwOpen, setPwOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const openId = params.get("application");

  const load = useCallback(() => {
    api.get("/agent/applications").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Errore nel caricamento"));
  }, []);
  useEffect(() => { if (user?.role === "agent" && !user.must_change_password) load(); }, [user, load]);

  if (user === null) return <main className="min-h-screen bg-white" />;
  if (!user) return <AdminLogin title="Area agenti" />;
  if (user.role !== "agent") return (
    <main className="flex min-h-screen items-center justify-center bg-white px-5 text-center">
      <p className="font-display text-4xl uppercase">Questa è l'area agenti. Sei collegata come admin: usa /admin.</p>
    </main>
  );
  if (user.must_change_password || pwOpen) return (
    <main data-testid="agent-change-password" className="min-h-screen bg-white px-5 pb-24 pt-32 text-[#1c1418]">
      <h1 className="mb-10 text-center font-display text-5xl uppercase md:text-7xl">Ciao {user.name}</h1>
      <ChangePassword forced={user.must_change_password} onDone={() => setPwOpen(false)} />
      {!user.must_change_password && <button onClick={() => setPwOpen(false)} className="mx-auto mt-6 block font-mono text-xs uppercase tracking-[0.2em] underline">Annulla</button>}
    </main>
  );

  const group = (x) => (x.category === "F_170_185" || x.category === "F_150_169" ? x.category : "OTHER");
  const counts = items.reduce((a, x) => ({ ...a, [group(x)]: (a[group(x)] || 0) + 1 }), {});
  const shown = items.filter((x) => group(x) === tab);

  return (
    <main data-testid="agent-dashboard" className="min-h-screen bg-white px-5 pb-24 pt-28 text-[#1c1418] md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#1c1418] pb-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/60">Area agente</p>
            <h1 className="font-display text-6xl uppercase leading-none md:text-8xl">{user.name}</h1>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => setPwOpen(true)} data-testid="agent-change-password-button" className="font-mono text-xs uppercase tracking-[0.2em] underline">Cambia password</button>
            <button onClick={logout} data-testid="agent-logout-button" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]"><LogOut className="h-4 w-4" /> Esci</button>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3 bg-[#f4f4f4] p-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em]">Il tuo link candidature</p>
          <code data-testid="agent-own-link" className="flex-1 truncate text-sm">{agentLink(user.ref)}</code>
          <button onClick={() => copy(agentLink(user.ref))} data-testid="agent-own-link-copy" className="flex items-center gap-2 bg-[#1c1418] px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-white">
            <Copy className="h-4 w-4 text-[#e9458f]" /> Copia
          </button>
        </div>
        <div className="mt-8 flex flex-wrap gap-6">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`agent-tab-${k}`}
              className={`font-display text-3xl uppercase ${tab === k ? "underline decoration-[#e9458f] decoration-4 underline-offset-8" : "text-[#1c1418]/40"}`}>{l} ({counts[k] || 0})</button>
          ))}
        </div>
        {tab === "F_150_169" && <p className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#1c1418]/60">Sotto 170 cm: non approvate automaticamente.</p>}
        <ul className="mt-6 divide-y divide-[#1c1418]/15 border-y border-[#1c1418]/15" data-testid="agent-applications-list">
          {shown.map((a) => (
            <li key={a.id}>
              <button onClick={() => setParams({ application: a.id })} data-testid={`agent-application-${a.id}`}
                className="flex w-full flex-wrap items-center gap-x-6 gap-y-1 py-4 text-left transition-colors hover:bg-[#f4f4f4]">
                <span className="font-mono text-xs">{new Date(a.created_at).toLocaleDateString("it-IT")}</span>
                <span className="font-display text-2xl uppercase">{a.first_name} {a.last_name}</span>
                <span className="font-mono text-xs">{a.height} cm · {a.age} anni</span>
                <span className={`ml-auto px-2 py-1 font-mono text-[11px] uppercase ${a.status === "approved" ? "bg-[#1c1418] text-white" : a.status === "rejected" ? "bg-[#1c1418]/10" : "bg-[#f4f4f4]"}`}>{STATUS_LABELS[a.status]}</span>
                {a.contract_status && <span className="font-mono text-[11px] uppercase">{CONTRACT[a.contract_status]}</span>}
              </button>
            </li>
          ))}
        </ul>
        {!shown.length && <p className="py-12 font-serif text-xl italic" data-testid="agent-applications-empty">Nessuna candidatura qui per ora.</p>}
      </div>
      {openId && <AgentApplicationDialog id={openId} onClose={() => setParams({})} onChange={load} />}
    </main>
  );
}
